/**
 * Tutorial Manager Module
 * Manages interactive onboarding workflows
 */

// Tutorial state
let currentStep = 0;
let isActive = false;
let currentWorkflow = null;

// DOM elements (will be created dynamically)
let overlay = null;
let spotlight = null;
let panel = null;

/**
 * Tutorial workflows definition
 */
const workflows = {
    'getting-started': [
        {
            target: '#upload-area',
            title: 'Upload Your Shapefile',
            description: 'Drag and drop your shapefile (.zip) here, or click to browse your files.',
            tip: 'Your shapefile should be in a .zip archive containing .shp, .shx, .dbf files.',
            position: 'bottom' // Position of instruction panel relative to target
        },
        // Add more steps here as needed
    ]
};

/**
 * Initialize tutorial system
 */
export function initializeTutorial() {
    createTutorialElements();
    console.log('Tutorial system initialized');
}

/**
 * Create tutorial overlay and elements
 */
function createTutorialElements() {
    // Remove any existing tutorial elements first
    const existingOverlay = document.querySelector('.tutorial-overlay');
    const existingSpotlight = document.querySelector('.tutorial-spotlight');
    const existingPanel = document.querySelector('.tutorial-panel');
    if (existingOverlay) existingOverlay.remove();
    if (existingSpotlight) existingSpotlight.remove();
    if (existingPanel) existingPanel.remove();

    // Create overlay
    overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay';
    overlay.style.display = 'none';
    document.body.appendChild(overlay);

    // Create spotlight
    spotlight = document.createElement('div');
    spotlight.className = 'tutorial-spotlight';
    spotlight.style.display = 'none';
    document.body.appendChild(spotlight);

    // Create instruction panel
    panel = document.createElement('div');
    panel.className = 'tutorial-panel';
    panel.style.display = 'none';
    document.body.appendChild(panel);

    // Click overlay to skip tutorial
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            endTutorial();
        }
    });
}

/**
 * Start a tutorial workflow
 * @param {string} workflowName - Name of the workflow to start
 */
export function startTutorial(workflowName = 'getting-started') {
    if (!workflows[workflowName]) {
        console.error(`Workflow "${workflowName}" not found`);
        return;
    }

    currentWorkflow = workflows[workflowName];
    currentStep = 0;
    isActive = true;

    // Show overlay
    overlay.style.display = 'block';
    overlay.classList.add('active');

    // Show first step
    showStep(currentStep);
}

/**
 * Show a specific tutorial step
 * @param {number} stepIndex - Index of the step to show
 */
function showStep(stepIndex) {
    if (!currentWorkflow || stepIndex >= currentWorkflow.length) {
        endTutorial();
        return;
    }

    const step = currentWorkflow[stepIndex];
    const targetElement = document.querySelector(step.target);

    if (!targetElement) {
        console.error(`Target element "${step.target}" not found`);
        endTutorial();
        return;
    }

    // Check if element is visible
    const rect = targetElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
        console.error(`Target element "${step.target}" is not visible`);
        endTutorial();
        return;
    }

    // Position and show spotlight
    positionSpotlight(targetElement);

    // Build and show instruction panel
    buildInstructionPanel(step, stepIndex);
    positionPanel(targetElement, step.position);
}

/**
 * Position spotlight on target element
 * @param {HTMLElement} element - Target element
 */
function positionSpotlight(element) {
    const rect = element.getBoundingClientRect();
    const padding = 8;

    spotlight.style.display = 'block';
    spotlight.style.top = `${rect.top - padding}px`;
    spotlight.style.left = `${rect.left - padding}px`;
    spotlight.style.width = `${rect.width + (padding * 2)}px`;
    spotlight.style.height = `${rect.height + (padding * 2)}px`;
}

/**
 * Build instruction panel content
 * @param {Object} step - Tutorial step data
 * @param {number} stepIndex - Current step index
 */
function buildInstructionPanel(step, stepIndex) {
    const totalSteps = currentWorkflow.length;
    const stepNumber = stepIndex + 1;

    panel.innerHTML = `
        <div class="tutorial-panel-header">
            <div class="tutorial-panel-icon">📍</div>
            <div class="tutorial-panel-title">${step.title}</div>
            <div class="tutorial-panel-step">${stepNumber}/${totalSteps}</div>
        </div>
        <div class="tutorial-panel-content">
            <p class="tutorial-panel-description">${step.description}</p>
            ${step.tip ? `<div class="tutorial-panel-tip">💡 ${step.tip}</div>` : ''}
        </div>
        <div class="tutorial-panel-actions">
            <button class="tutorial-btn tutorial-btn-skip">Skip Tutorial</button>
            <button class="tutorial-btn tutorial-btn-next">
                ${stepIndex < totalSteps - 1 ? 'Next' : 'Got It!'}
            </button>
        </div>
    `;

    // Add event listeners
    panel.querySelector('.tutorial-btn-skip').addEventListener('click', endTutorial);
    panel.querySelector('.tutorial-btn-next').addEventListener('click', nextStep);
}

/**
 * Position instruction panel relative to target
 * @param {HTMLElement} element - Target element
 * @param {string} position - Position preference (top, bottom, left, right)
 */
function positionPanel(element, position = 'bottom') {
    panel.style.display = 'block';

    const rect = element.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const spacing = 20;

    let top, left;

    switch (position) {
        case 'top':
            top = rect.top - panelRect.height - spacing;
            left = rect.left + (rect.width / 2) - (panelRect.width / 2);
            break;
        case 'bottom':
            top = rect.bottom + spacing;
            left = rect.left + (rect.width / 2) - (panelRect.width / 2);
            break;
        case 'left':
            top = rect.top + (rect.height / 2) - (panelRect.height / 2);
            left = rect.left - panelRect.width - spacing;
            break;
        case 'right':
            top = rect.top + (rect.height / 2) - (panelRect.height / 2);
            left = rect.right + spacing;
            break;
        default:
            top = rect.bottom + spacing;
            left = rect.left + (rect.width / 2) - (panelRect.width / 2);
    }

    // Keep panel within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 10) left = 10;
    if (left + panelRect.width > viewportWidth - 10) {
        left = viewportWidth - panelRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + panelRect.height > viewportHeight - 10) {
        top = viewportHeight - panelRect.height - 10;
    }

    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
}

/**
 * Move to next step
 */
function nextStep() {
    currentStep++;
    if (currentStep < currentWorkflow.length) {
        showStep(currentStep);
    } else {
        endTutorial();
    }
}

/**
 * End tutorial
 */
export function endTutorial() {
    isActive = false;
    currentStep = 0;
    currentWorkflow = null;

    if (overlay) {
        overlay.classList.remove('active');
        overlay.style.display = 'none';
    }
    if (spotlight) {
        spotlight.style.display = 'none';
    }
    if (panel) {
        panel.innerHTML = '';
        panel.style.display = 'none';
    }

    console.log('Tutorial ended');
}

/**
 * Check if tutorial is active
 */
export function isTutorialActive() {
    return isActive;
}

/**
 * Add a new workflow
 * @param {string} name - Workflow name
 * @param {Array} steps - Array of tutorial steps
 */
export function addWorkflow(name, steps) {
    workflows[name] = steps;
    console.log(`Workflow "${name}" added`);
}
